"use client";

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// @mui
import { useTheme } from "@mui/material/styles";
import {
  Stack,
  Container,
  Card,
  Typography,
  Grid,
  Box,
  Link,
  Avatar,
  Button,
  CardContent,
  Modal,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  AlertTitle,
  Snackbar,
  Tooltip,
} from "@mui/material";
// form
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider } from "../../components/hook-form";
import Iconify from "components/iconify";
import customFetch from "hooks/fetchWrapper";

export default function RouteFilter() {
  const theme = useTheme();
  const isMd = theme.breakpoints.up("md");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedRouteDetails, setSelectedRouteDetails] = useState(null);
  const [routeData, setRouteData] = useState([]);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedDetailRoute, setSelectedDetailRoute] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);

  // form
  const FilterSchema = Yup.object().shape({
    emirate: Yup.string().required("Emirates is required"),
    area: Yup.string(),
    route: Yup.string(),
  });

  const defaultValues = {
    emirate: "",
    area: "",
    route: "",
  };

  const methods = useForm({
    resolver: yupResolver(FilterSchema),
    defaultValues,
  });

  const { handleSubmit, watch, setValue, reset, getValues } = methods;
  const selectedEmirate = watch("emirate");
  const selectedArea = watch("area");
  const selectedRoute = watch("route");

  // Initialize with query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emirateParam = queryParams.get("emirate");
    const areaParam = queryParams.get("area");
    const routeParam = queryParams.get("route");

    console.log("Query params:", { emirate: emirateParam, area: areaParam, route: routeParam });

    // Fetch emirates first
    fetchEmirates().then(() => {
      // If we have emirate param, set it and fetch related data
      if (emirateParam) {
        setValue("emirate", emirateParam);

        // Fetch areas for this emirate
        fetchAreasByEmirate(emirateParam).then(() => {
          if (areaParam) {
            setValue("area", areaParam);
          }
        });

        // Fetch routes for this emirate
        fetchRoutesByEmirate(emirateParam).then(() => {
          if (routeParam) {
            // Ensure route is set after routes are fetched
            setTimeout(() => {
              setValue("route", routeParam);
              fetchRouteDetails(routeParam);
            }, 300);
          }
        });
      }
    });
  }, [location.search]);

  // Watch for route changes and fetch details
  useEffect(() => {
    if (selectedRoute && !initialLoad) {
      fetchRouteDetails(selectedRoute);
      setOpenDetailModal(false); // Close modal when selecting new route
    }
  }, [selectedRoute, initialLoad]);

  // Fetch areas when emirate changes
  useEffect(() => {
    if (selectedEmirate && !initialLoad) {
      fetchAreasByEmirate(selectedEmirate);
      fetchRoutesByEmirate(selectedEmirate);

      // Reset area and route selection when emirate changes
      setValue("area", "");
      setValue("route", "");
      setSelectedRouteDetails(null);
      setRouteData([]);
      setOpenDetailModal(false);
    }
    if (selectedEmirate) {
      setInitialLoad(false);
    }
  }, [selectedEmirate]);

  // Filter routes when area changes
  useEffect(() => {
    if (selectedArea && !initialLoad) {
      // Reset route selection when area changes
      setValue("route", "");
      setSelectedRouteDetails(null);
      setRouteData([]);
      setOpenDetailModal(false);
    }
  }, [selectedArea]);

  const onSubmit = async (data) => {
    setLoading(true);
    console.log("Form submitted with:", data);
    setTimeout(() => {
      const queryParams = new URLSearchParams({
        emirate: data.emirate,
        area: data.area || "",
        route: data.route || "",
      }).toString();
      navigate(`/services/bus-routes/search?${queryParams}`);
      setLoading(false);
    }, 800);
  };

  const fetchEmirates = async () => {
    try {
      const response = await customFetch("emirates");
      // Handle both possible response formats (direct array or {status, data})
      const emiratesData = Array.isArray(response)
        ? response
        : response.status && Array.isArray(response.data)
        ? response.data
        : [];
      setEmirates(emiratesData);
      return emiratesData;
    } catch (error) {
      console.error("Error fetching emirates:", error);
      return [];
    }
  };

  const fetchAreasByEmirate = async (emirateId) => {
    try {
      const response = await customFetch(`areas/emirate/${emirateId}`);
      const areasData = response.status && Array.isArray(response.data) ? response.data : [];
      setAreas(areasData);
      return areasData;
    } catch (error) {
      console.error("Error fetching areas by emirate:", error);
      setAreas([]);
      return [];
    }
  };

  const fetchRoutesByEmirate = async (emirateId) => {
    try {
      const response = await customFetch(`routes/by-emirate/${emirateId}`);
      const routesData = response.status && Array.isArray(response.data) ? response.data : [];
      setRoutes(routesData);
      return routesData;
    } catch (error) {
      console.error("Error fetching routes by emirate:", error);
      setRoutes([]);
      return [];
    }
  };

  const fetchRouteDetails = async (routeId) => {
    try {
      setLoading(true);
      const response = await customFetch(
        `driverandrouteswithtime?driverId=&routeId=${routeId === "0" ? "" : routeId}`
      );

      if (response.status && response.data && response.data.length > 0) {
        const jsonData = JSON.parse(response.data[0]["JSON_F52E2B61-18A1-11d1-B105-00805F49916B"]);
        console.log("Route details:", jsonData);

        if (jsonData && jsonData.length > 0) {
          if (routeId === "0") {
            // For "All Routes", just display the first route initially
            setSelectedRouteDetails(jsonData[0]);
            setRouteData(jsonData);
          } else {
            // Find the route that matches our selected route ID
            const routeInfo = jsonData.find(
              (route) =>
                route.RouteID?.toString() === routeId || route.RouteID === Number.parseInt(routeId)
            );
            setSelectedRouteDetails(routeInfo || jsonData[0]);
            setRouteData(jsonData);
          }
        } else {
          // No data found
          setSelectedRouteDetails(null);
          setRouteData([]);
        }
      } else {
        // No data found
        setSelectedRouteDetails(null);
        setRouteData([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching route details:", error);
      setSelectedRouteDetails(null);
      setRouteData([]);
      setLoading(false);
    }
  };

  // Handle opening the detail modal
  const handleViewRouteDetails = (routeDetail) => {
    setSelectedDetailRoute(routeDetail);
    setOpenDetailModal(true);
  };

  // Close the detail modal
  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
  };

  // Copy phone number to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopySnackbar(true);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Create options for the dropdowns
  const emirateOptions = [
    { label: "Select Emirates", value: "" },
    ...emirates.map((emirate) => ({
      label: emirate.name,
      value: emirate.eid.toString(),
    })),
  ];

  const areaOptions = [
    { label: "Select Area", value: "" },
    ...areas.map((area) => ({
      label: area.code ? `${area.code} - ${area.emirateName}` : area.emirateName,
      value: area.aid.toString(),
    })),
  ];

  const routeOptions = [
    { label: "Select Route", value: "" },
    { label: "ALL Routes", value: "0" },
    ...routes
      .filter((route) => !selectedArea || route.aid.toString() === selectedArea)
      .map((route) => ({
        label: `${route.routename} - ${route.routeno}`,
        value: route.rid.toString(),
      })),
  ];

  // Calculate grid size based on number of items
  const getGridSizes = (itemCount) => {
    if (itemCount === 1) {
      return { xs: 12, sm: 12, md: 12 };
    } else if (itemCount === 2) {
      return { xs: 12, sm: 6, md: 6 };
    } else {
      return { xs: 12, sm: 6, md: 4 };
    }
  };

  // Get all routes to display
  const allRoutesToDisplay =
    selectedRoute === "0" && routeData.length > 0
      ? routeData
      : selectedRouteDetails
      ? [selectedRouteDetails]
      : [];

  // Grid size calculation
  const gridSizes = getGridSizes(allRoutesToDisplay.length);

  // Custom styled dropdown
  const CustomDropdown = ({ name, label, options, disabled = false }) => (
    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
      <InputLabel id={`${name}-label`}>{label}</InputLabel>
      <Select
        labelId={`${name}-label`}
        value={methods.watch(name)}
        onChange={(e) => {
          setValue(name, e.target.value);
          // Auto-submit form when dropdown changes
          setTimeout(() => {
            handleSubmit(onSubmit)();
          }, 100);
        }}
        label={label}
        disabled={disabled}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#6ca892",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#5b9780",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#6ca892",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            py: 1.5,
          },
          "& .MuiSvgIcon-root": {
            color: "#6ca892",
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Container>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
          position="relative"
          mb={4}
          mt={5}
        >
          <Box sx={{ width: "100%" }}>
            <CustomDropdown name="emirate" label="Emirates" options={emirateOptions} />
          </Box>
          <Box sx={{ width: "100%" }}>
            <CustomDropdown
              name="area"
              label="Area"
              options={areaOptions}
              disabled={!selectedEmirate}
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <CustomDropdown
              name="route"
              label="Route"
              options={routeOptions}
              disabled={!selectedEmirate}
            />
          </Box>

          {/* Search button removed as requested */}
        </Stack>
      </FormProvider>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ textAlign: "center", my: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress sx={{ color: "#6ca892" }} />
        </Box>
      )}

      {/* No records message */}
      {!loading && selectedEmirate && allRoutesToDisplay.length === 0 && (
        <Alert
          severity="info"
          sx={{
            my: 4,
            borderRadius: 2,
            border: "1px solid rgba(108, 168, 146, 0.3)",
            "& .MuiAlert-icon": {
              color: "#6ca892",
            },
          }}
        >
          <AlertTitle>No Records Found</AlertTitle>
          There are no routes available for your selected criteria. Please try different filters.
        </Alert>
      )}

      {/* Route Cards Grid */}
      <Grid container spacing={3}>
        {allRoutesToDisplay.map((route) => {
          const gridProps = allRoutesToDisplay.length < 3 ? gridSizes : { xs: 12, sm: 6, md: 4 };
          return (
            <Grid item {...gridProps} key={route.RouteID || route.routename}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: theme.shadows[2],
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: theme.shadows[6],
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#6ca892",
                    p: 2,
                    color: "white",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    src="https://www.mauicardiovascularsymposium.com/wp-content/uploads/2019/08/dummy-profile-pic-300x300.png"
                    sx={{
                      width: 80,
                      height: 80,
                      border: "3px solid white",
                      mb: 1,
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight="bold" align="center" noWrap>
                    {route.DriverName}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Iconify icon="mdi:phone" fontSize="small" />
                    <Typography variant="caption">{route.ContactNumber}</Typography>
                    <Tooltip title="Copy phone number">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(route.ContactNumber)}
                        sx={{
                          color: "white",
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                          },
                        }}
                      >
                        <Iconify icon="mdi:content-copy" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color="#6ca892">
                      Route {route.routeno}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#6ca892",
                        color: "white",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Iconify icon="mdi:bus" fontSize="small" />
                      <Typography variant="caption" fontWeight="bold">
                        Bus {route.BusNumber}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2" gutterBottom>
                    {route.routename}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Link
                      href={route.map}
                      target="_blank"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#6ca892",
                        textDecoration: "none",
                        fontSize: "0.875rem",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <Iconify icon="mdi:map" sx={{ mr: 0.5 }} fontSize="small" />
                      View Map
                    </Link>

                    {route.pickups && route.pickups.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewRouteDetails(route)}
                        startIcon={<Iconify icon="mdi:chevron-right" />}
                        sx={{
                          borderColor: "#6ca892",
                          color: "#6ca892",
                          fontSize: "0.75rem",
                          py: 0.5,
                          "&:hover": {
                            borderColor: "#5b9780",
                            backgroundColor: "rgba(108, 168, 146, 0.1)",
                          },
                        }}
                      >
                        View Details
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Detail Modal */}
      <Modal
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        aria-labelledby="route-detail-modal-title"
        aria-describedby="route-detail-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 1200,
            maxHeight: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 1,
            overflow: "auto",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
          >
            <Typography id="route-detail-modal-title" variant="h6" component="h2">
              Route {selectedDetailRoute?.routeno} - Pickup/Drop-off Points
            </Typography>
            <IconButton onClick={handleCloseDetailModal} size="small">
              <Iconify icon="mdi:close" />
            </IconButton>
          </Box>

          {/* Custom Table Layout */}
          {selectedDetailRoute?.pickups && selectedDetailRoute.pickups.length > 0 ? (
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  borderRadius: 1,
                  overflow: "hidden",
                  border: "1px solid #e0e0e0",
                }}
              >
                {/* Table Headers */}
                {[
                  "ORDER",
                  "PICK UP CODE",
                  "BUS STOP",
                  "AREA CODE",
                  "PICK UP TIME",
                  "DROP OFF TIME",
                ].map((header, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      bgcolor: "#f5f5f5",
                      p: 2,
                      fontWeight: "bold",
                      borderBottom: "1px solid #e0e0e0",
                      borderRight: idx !== 5 ? "1px solid #e0e0e0" : "none",
                      textAlign: "center",
                    }}
                  >
                    {header}
                  </Box>
                ))}

                {/* Table Data */}
                {selectedDetailRoute.pickups.map((pickup, index) => (
                  <React.Fragment key={index}>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom:
                          index < selectedDetailRoute.pickups.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        borderRight: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom:
                          index < selectedDetailRoute.pickups.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        borderRight: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      {pickup.PickupCodes}
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom:
                          index < selectedDetailRoute.pickups.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        borderRight: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      {pickup.BustopNames}
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom:
                          index < selectedDetailRoute.pickups.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        borderRight: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      {selectedDetailRoute.AreaCode || "N/A"}
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom:
                          index < selectedDetailRoute.pickups.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        borderRight: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      {pickup.PickupTimes || "N/A"}
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom:
                          index < selectedDetailRoute.pickups.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        textAlign: "center",
                      }}
                    >
                      {pickup.DropOffTimes || "N/A"}
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          ) : (
            <Alert severity="info">No pickup/drop-off points available for this route.</Alert>
          )}
        </Box>
      </Modal>

      {/* Copy success notification */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(false)}
        message="Phone number copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
}
